import apiClient from './api';
import type { Tenant, PaginatedResponse, PaginationParams } from '@/types';

export const tenantService = {
  /**
   * Get all tenants
   */
  async getTenants(params?: PaginationParams): Promise<PaginatedResponse<Tenant>> {
    const { data } = await apiClient.get<PaginatedResponse<Tenant>>('/tenants', { params });
    return data;
  },

  /**
   * Get current tenant
   */
  async getCurrentTenant(): Promise<Tenant> {
    const { data } = await apiClient.get<Tenant>('/tenants/current');
    return data;
  },

  /**
   * Get single tenant by ID
   */
  async getTenant(id: string): Promise<Tenant> {
    const { data} = await apiClient.get<Tenant>(`/tenants/${id}`);
    return data;
  },

  /**
   * Create new tenant
   */
  async createTenant(tenantData: Partial<Tenant>): Promise<Tenant> {
    const { data } = await apiClient.post<Tenant>('/tenants', tenantData);
    return data;
  },

  /**
   * Update tenant
   */
  async updateTenant(id: string, tenantData: Partial<Tenant>): Promise<Tenant> {
    const { data } = await apiClient.put<Tenant>(`/tenants/${id}`, tenantData);
    return data;
  },

  /**
   * Delete tenant
   */
  async deleteTenant(id: string): Promise<void> {
    await apiClient.delete(`/tenants/${id}`);
  },

  /**
   * Suspend tenant
   */
  async suspendTenant(id: string): Promise<{ message: string }> {
    const { data } = await apiClient.post(`/tenants/${id}/suspend`);
    return data;
  },

  /**
   * Activate tenant
   */
  async activateTenant(id: string): Promise<{ message: string }> {
    const { data } = await apiClient.post(`/tenants/${id}/activate`);
    return data;
  },

  /**
   * Get available features
   */
  async getAvailableFeatures(): Promise<string[]> {
    const { data } = await apiClient.get('/tenants/features');
    return data;
  },

  /**
   * Update tenant features
   */
  async updateFeatures(id: string, features: string[]): Promise<{ message: string }> {
    const { data } = await apiClient.post(`/tenants/${id}/features`, { features });
    return data;
  },

  /**
   * Get tenant audit logs
   */
  async getAuditLogs(id: string, params?: PaginationParams) {
    const { data } = await apiClient.get(`/tenants/${id}/audit-logs`, { params });
    return data;
  },
};
